import { ICheckboxFilterItem } from '../../common/models/filter/ICheckboxFilterItem';
import { pipe, ifElse, map, identity, append, find, includes, difference, reduce } from 'ramda';
import { CandidateFilterType } from '../../common/enums/CandidateFilterType';
import { PositionFilterType } from '../../common/enums/PositionFilterType';
import SidebarFilterItem from '../CheckboxFilterItem';
import { Node } from '../../common/models/common/Node';
import React from 'react';

const emptyNode = {
  value: '',
  label: ''
};

const getItem = (
  id: CandidateFilterType | PositionFilterType,
  items: ICheckboxFilterItem[]
) => find(item => item.id === id, items);

const getNodes = (item?: ICheckboxFilterItem): Node => {
  let children;
  if (item && item.children) {
    children = map(
      child => ({
        label: (
          <SidebarFilterItem
            id={child.id}
            key={child.id}
            name={child.name}
            count={child.count || 0}
            color={child.color}
          />
        ),
        value: child.id
      }),
      item.children
    );
  }

  if (item) {
    return {
      value: item.id,
      label: (
        <SidebarFilterItem
          id={item.id}
          key={item.id}
          name={item.name}
          count={item.count || 0}
          color={item.color}
        />
      ),
      children
    };
  }
  return emptyNode;
};

export const mapTree = (items: ICheckboxFilterItem[]) => {
  const itemsId = map(item => item.id, items);
  return pipe(
    ifElse(
      () => includes(CandidateFilterType.AllActive, itemsId),
      append(getNodes(getItem(CandidateFilterType.AllActive, items))),
      identity
    ),
    ifElse(
      () => includes(CandidateFilterType.AllInactive, itemsId),
      append(getNodes(getItem(CandidateFilterType.AllInactive, items))),
      identity
    ),
    ifElse(
      () => includes(PositionFilterType.AllActivePositions, itemsId),
      append(getNodes(getItem(PositionFilterType.AllActivePositions, items))),
      identity
    ),
    ifElse(
      () => includes(PositionFilterType.InactivePositions, itemsId),
      append(getNodes(getItem(PositionFilterType.InactivePositions, items))),
      identity
    ),
    ifElse(
      () => includes(PositionFilterType.AllPositions, itemsId),
      append(getNodes(getItem(PositionFilterType.AllPositions, items))),
      identity
    )
  )([] as Node[]);
};

const allActive = [
  CandidateFilterType.WaitingForCandidate,
  CandidateFilterType.NoSurveysComplete,
  CandidateFilterType.OnePlusSurveysComplete,
  CandidateFilterType.AllSurveysComplete
];

const allInactive = [
  CandidateFilterType.InactiveHired,
  CandidateFilterType.InactiveHibernate
];

const allActivePositions = [
  PositionFilterType.NotStarted,
  PositionFilterType.InProcess,
  PositionFilterType.Complete
];

const replaceParent = (id: string) => {
  switch (id) {
    case CandidateFilterType.AllActive:
      return allActive;
    case PositionFilterType.AllActivePositions:
      return allActivePositions;
    case CandidateFilterType.AllInactive:
      return allInactive;
    default:
      return [id];
  }
};

export const replaceParentInArray = (ids: string[]) => (
  reduce((acc, id) => acc.concat(replaceParent(id)), [] as string[], ids)
);

export const replaceChildren = (ids: string[]) => {
  let result = ids;

  if (!difference(allActive, result).length) {
    result = difference(result, allActive);
    result.push(CandidateFilterType.AllActive);
  }
  if (!difference(allInactive, result).length) {
    result = difference(result, allInactive);
    result.push(CandidateFilterType.AllInactive);
  }
  if (!difference(allActivePositions, result).length) {
    result = difference(result, allActivePositions);
    result.push(PositionFilterType.AllActivePositions);
  }
  return result;
};
